import { useCallback, useEffect, useState, type MutableRefObject } from "react";
import type { DeepgramStreamingEngine } from "@/lib/asr/deepgramStreamingEngine";
import type { DeepgramDiagnostics } from "@/lib/asr/deepgramDiagnostics";
import { getDeepgramDiagnosticsRingBuffer } from "@/lib/asr/deepgramDiagnostics";

// Phase 1 robustness pass surface for platform_dev — see
// docs/planning/deepgram_streaming_robustness.md §3 Phase 1 bullet 3.
// Polls `engine.getDiagnostics()` every 1s while open so a dev can watch
// counters tick in real time without reaching into the engine.
// §4 Q4: ring buffer + "Copy diagnostics" export (no transcript text).

type Props = {
  asrEngineRef: MutableRefObject<DeepgramStreamingEngine | null>;
};

function formatAgeMs(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MiB`;
}

function timestampOrDash(ts: number | null): string {
  if (ts == null) return "—";
  try {
    return new Date(ts).toLocaleTimeString();
  } catch {
    return "—";
  }
}

async function exportDiagnosticsJson(
  engine: DeepgramStreamingEngine | null,
): Promise<"clipboard" | "download"> {
  const ring = getDeepgramDiagnosticsRingBuffer();
  const current = engine ? engine.getDiagnostics() : null;
  const json = JSON.stringify(ring.buildExport(current), null, 2);
  try {
    await navigator.clipboard.writeText(json);
    return "clipboard";
  } catch {
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `deepgram-asr-diagnostics-${Date.now()}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    return "download";
  }
}

export function LeadDeviceAsrDiagnosticsPanel({ asrEngineRef }: Props) {
  const [snapshot, setSnapshot] = useState<DeepgramDiagnostics | null>(null);
  const [open, setOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const ring = getDeepgramDiagnosticsRingBuffer();
    const tick = () => {
      const engine = asrEngineRef.current;
      const next = engine ? engine.getDiagnostics() : null;
      if (next) ring.pushSnapshot(next);
      setSnapshot(next);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [open, asrEngineRef]);

  const onCopyDiagnostics = useCallback(async () => {
    setCopyStatus(null);
    try {
      const via = await exportDiagnosticsJson(asrEngineRef.current);
      setCopyStatus(via === "clipboard" ? "Copied to clipboard." : "Downloaded JSON file.");
    } catch {
      setCopyStatus("Export failed — check console.");
    }
  }, [asrEngineRef]);

  const now = Date.now();
  const sinceConnected =
    snapshot?.lastConnectedAtMs != null ? Math.max(0, now - snapshot.lastConnectedAtMs) : null;
  const sinceResult =
    snapshot?.lastResultAtMs != null ? Math.max(0, now - snapshot.lastResultAtMs) : null;

  return (
    <details
      className="mt-4 text-left text-sm text-gray-600"
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer text-violet-700 text-base">
        Developer: ASR diagnostics (Deepgram)
      </summary>

      <div className="mt-4 border border-violet-200 rounded-xl p-4 bg-violet-50/40 space-y-2 font-mono text-xs">
        {snapshot == null ? (
          <p className="text-gray-500">Engine not initialized yet — start a Leads listening session.</p>
        ) : (
          <>
            <Row label="Session started" value={timestampOrDash(snapshot.sessionStartedAtMs)} />
            <Row label="Last connected" value={`${timestampOrDash(snapshot.lastConnectedAtMs)} (${formatAgeMs(sinceConnected)} ago)`} />
            <Row label="Last Results frame" value={`${timestampOrDash(snapshot.lastResultAtMs)} (${formatAgeMs(sinceResult)} ago)`} />
            <Row label="Bytes sent (session)" value={`${formatBytes(snapshot.bytesSent)} (${snapshot.framesSent.toLocaleString()} frames)`} />
            <Row label="Dropped frames (backpressure)" value={`${snapshot.droppedFrames.toLocaleString()}`} />
            <Row label="KeepAlives sent (silence)" value={`${snapshot.keepalivesSent.toLocaleString()}`} />
            <Row label="Reconnect attempts (active streak)" value={`${snapshot.reconnectAttempts}`} />
            <Row label="Reconnects succeeded (lifetime)" value={`${snapshot.totalReconnectsSucceeded}`} />
            <Row label="Last close" value={`${snapshot.lastCloseCode ?? "—"}${snapshot.lastCloseReason ? ` "${snapshot.lastCloseReason}"` : ""}`} />
            <Row label="PCM sample rate (session)" value={snapshot.pcmSampleRateHz != null ? `${snapshot.pcmSampleRateHz} Hz` : "—"} />
            <Row label="Audio path" value={snapshot.audioPath ?? "—"} />
            <Row label="navigator.connection.effectiveType" value={snapshot.effectiveType ?? "(unavailable on this browser)"} />
            <p className="pt-1 text-[10px] text-violet-900/70">
              Snapshot refreshes every 1s while this panel is open. See <code className="break-all">docs/planning/deepgram_streaming_robustness.md</code>.
            </p>
          </>
        )}

        <div className="pt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="rounded-lg border border-violet-300 bg-white px-3 py-1.5 text-xs text-violet-800 hover:bg-violet-50"
            onClick={() => void onCopyDiagnostics()}
          >
            Copy diagnostics
          </button>
          {copyStatus ? <span className="text-[10px] text-violet-900/80">{copyStatus}</span> : null}
        </div>
        <p className="text-[10px] text-violet-900/60">
          Exports ~5 min of snapshots (while open) + unexpected-close events. No transcript text.
        </p>
      </div>
    </details>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3">
      <span className="text-violet-900/80">{label}:</span>
      <span className="text-gray-900">{value}</span>
    </div>
  );
}
